import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import type { ECharts, EChartsOption, ECElementEvent } from 'echarts';
import { ChartContext, ChartDataItem } from './chart.models';
import { buildChartOptions } from './chart.utils';

@Component({
  selector: 'app-chart',
  templateUrl: './chart.component.html',
  styleUrls: ['./chart.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChartComponent implements OnChanges, OnDestroy {
  @Input() context!: ChartContext;
  @Output() readonly itemClick = new EventEmitter<ChartDataItem>();

  options: EChartsOption = {};
  hasData = false;
  heatmapHeight = 70;
  private chartWidth = 840;
  private resizeObserver?: ResizeObserver;

  constructor(private readonly changeDetectorRef: ChangeDetectorRef) {}

  ngOnChanges(): void {
    this.hasData = (this.context.type === 'heatmap' || this.context.type === 'tree') ? this.context.data.length > 0
      : this.context.type === 'line' || this.context.type === 'bar'
      ? this.context.data.some(item => item.value !== null && Number.isFinite(item.value))
      : this.context.type === 'ring-gauge' || (this.context.type === 'gauge'
      ? this.context.data.length > 0
      : this.context.data.some(item => item.value > 0));
    this.updateOptions();
    if (this.context.loading || this.context.error || !this.hasData) {
      this.resizeObserver?.disconnect();
    }
  }

  private updateOptions(): void {
    let context = this.context;
    if (context.type === 'heatmap') {
      const compact = this.chartWidth < 360;
      const gap = compact ? 3 : 5;
      const columns = Math.max(1, Math.min(context.columns || 15, Math.floor((this.chartWidth + gap) / (22 + gap)), context.data.length || 1));
      this.heatmapHeight = Math.max(1, Math.ceil(context.data.length / columns)) * (compact ? 31 : 40) + 30;
      context = { ...context, columns };
    }
    this.options = buildChartOptions(context, (context.type === 'tree' || context.type === 'ring-gauge' || context.type === 'line' || context.type === 'bar' || context.type === 'heatmap')
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  onChartInit(chart: ECharts): void {
    this.resizeObserver?.disconnect();
    const container = chart.getDom();
    this.resizeObserver = new ResizeObserver(() => {
      if (chart.isDisposed()) return;
      const width = container.clientWidth;
      if (this.context.type === 'heatmap' && width > 0 && width !== this.chartWidth) {
        this.chartWidth = width;
        this.updateOptions();
        this.changeDetectorRef.detectChanges();
      }
      const height = this.context.type === 'heatmap' ? this.heatmapHeight : container.clientHeight;
      // Resizing an unchanged chart interrupts its entrance animation.
      if (width > 0 && height > 0 && (width !== chart.getWidth() || height !== chart.getHeight())) {
        chart.resize({ width, height });
      }
    });
    this.resizeObserver.observe(container);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  onChartClick(event: ECElementEvent): void {
    if (event.componentType !== 'series' || this.context.type === 'tree') return;
    const item = this.context.data[event.dataIndex];
    if (item && item.value !== null) this.itemClick.emit({ name: item.name, value: item.value });
  }
}
